import React, { useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'
import OfferPrice from './Price/OfferPrice'
import { PriceContainer, PricingWrapper, SingleValueWrapper } from './styles'
import { connect } from 'react-redux'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import Pill from 'LuxKit/Pill'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { getVillaCapacityText } from 'lib/homesAndVillas/getVillaCapacityText'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'

interface Props {
  offer: App.TourOffer | App.TourOfferSummary
  className?: string
  infoBarMedium?: boolean
  infoBarMini?: boolean
  infoBar?: boolean
  showFlights?: boolean
  originAirports: Array<App.Airport>
  flightOrigin?: string
}

function OfferPricing(props: Props) {
  const {
    originAirports,
    infoBarMedium,
    infoBarMini,
    className,
    infoBar,
    showFlights,
    offer,
    flightOrigin = '',
  } = props

  const {
    tileDurationLabel,
    parentType,
    typeLabel,
    saleUnit,
    offerFlightsEnabled,
    flightCacheDisabled,
    lowestPricePackage,
  } = offer

  const {
    name,
    roomOccupancy,
    shouldDisplayValue,
    roomPolicyDescription,
  } = lowestPricePackage

  const isCruise = offer.holidayTypes.includes('Cruises')
  const noFlightsFromOrigin = offer.flightDestinationPort === flightOrigin
  const originAirport = useMemo(() => originAirports.find(a => a.code === flightOrigin), [originAirports, flightOrigin])
  const flightCacheOff = showFlights && (flightCacheDisabled || !offer.flightPrices[flightOrigin])

  const flightPrice = offer.flightPrices[flightOrigin] || 0
  const pricing = useOfferPackagePrice(lowestPricePackage, offer, { flightPrice })

  const bundledPrice = pricing.totalPrice + (pricing.flightPrice || 0)
  const bundledValue = pricing.totalValue + (pricing.flightPrice || 0)

  const hotelDiscountPercent = getValueOffPercent(pricing.totalValue, pricing.totalPrice)
  const bundledDiscountPercent = getValueOffPercent(bundledValue, bundledPrice)
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscountPercent = !offer.isDiscountPillHidden && !offer.bundledWithFlightsOnly && highestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const isHomesAndVillas = isVillaOffer(offer)
  const capacityLabel = isHomesAndVillas ? getVillaCapacityText(offer) : null

  const shouldTrackPrice = !!pricing?.totalPrice
  const priceTrackedRef = useRef(false)

  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: pricing.totalPrice.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, pricing.totalPrice, offer?.id])

  return <>
    {shouldShowDiscountPercent && !isCruise &&
      <SingleValueWrapper variant="medium" weight="bold">
        <Pill kind="primary" variant="success">Save up to {highestDiscountPercent}%</Pill>
      </SingleValueWrapper>}
    <PricingWrapper>
      <PriceContainer className={clsx(className, {
        'cache-off': flightCacheOff && offerFlightsEnabled,
      })}
      >
        <OfferPrice
          offer={offer}
          className={className}
          durationLabel={tileDurationLabel}
          infoBarMedium={infoBarMedium}
          infoBarMini={infoBarMini}
          flightCacheOff={flightCacheOff}
          roomOccupancy={isCruise ? name : parentType === OFFER_TYPE_TOUR ? roomOccupancy : undefined}
          offerSaleUnit={saleUnit}
          label={capacityLabel ?? typeLabel}
          shouldShowValue={shouldDisplayValue}
          showFlights={showFlights}
          price={pricing.totalPrice}
          value={pricing.totalValue}
          roomPolicyDescription={roomPolicyDescription}
          availablePackage={lowestPricePackage}
          highestDiscountPercent={highestDiscountPercent}
          isCruise={isCruise}
        />
      </PriceContainer>
      {offerFlightsEnabled && showFlights && <PriceContainer className={clsx(className, {
        'cache-off': flightCacheOff && offerFlightsEnabled,
      })}
      >
        <OfferPrice
          offer={offer}
          hasClause
          isFlightPrice
          className={className}
          durationLabel={tileDurationLabel}
          flightCacheOff={flightCacheOff}
          infoBarMedium={infoBarMedium}
          infoBarMini={infoBarMini}
          infoBar={infoBar}
          offerSaleUnit="package"
          label={`${typeLabel} + Flights`}
          shouldShowValue={shouldDisplayValue && !flightCacheOff}
          showFlights={showFlights}
          price={bundledPrice}
          value={bundledValue}
          originAirport={originAirport}
          noFlightsFromOrigin={noFlightsFromOrigin}
          roomPolicyDescription={roomPolicyDescription}
          availablePackage={lowestPricePackage}
          highestDiscountPercent={highestDiscountPercent}
          isCruise={isCruise}
        />
      </PriceContainer>}
    </PricingWrapper>
  </>
}

function mapStateToProps(state: App.State) {
  return {
    originAirports: state.geo.originAirports,
  }
}

export default connect(mapStateToProps)(OfferPricing)
